import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2, Heart } from "lucide-react";
import { ASSETS, api, formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const LABELS = ["", "Not great", "Could be better", "Good", "Great", "Amazing!"];

export default function StaffReview() {
  const { code } = useParams();
  const [staff, setStaff] = useState(undefined);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/referral/${code}`).then(({ data }) => setStaff(data)).catch(() => setStaff(null));
  }, [code]);

  async function submit(e) {
    e.preventDefault();
    if (!rating) return toast.error("Tap a star rating first");
    setBusy(true);
    try {
      await api.post("/staff-reviews", { staff_code: code, rating, comment, name });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  const first = staff?.name?.split(" ")[0];
  const shown = hover || rating;

  return (
    <div data-testid="staff-review-page" className="min-h-screen bg-[var(--trio-bg)] flex flex-col items-center px-6 py-12">
      <img src={ASSETS.logo} alt="Trio" className="h-12 w-auto mb-8" />
      {staff === undefined && <Loader2 className="w-10 h-10 animate-spin text-[var(--trio-primary)]" />}
      {staff === null && <h1 data-testid="staff-review-not-found" className="font-display uppercase text-3xl">Link not found</h1>}
      {staff && done && (
        <div data-testid="staff-review-thanks" className="text-center max-w-sm">
          <Heart className="w-14 h-14 mx-auto text-[var(--trio-primary)] mb-4" />
          <h1 className="font-display uppercase text-4xl tracking-tight mb-2">Thank You!</h1>
          <p className="text-sm text-[var(--trio-muted)]">Your review means a lot to {first} and the whole Trio crew.</p>
          <Link to="/" className="inline-block mt-6 text-xs uppercase tracking-widest underline">Visit triohsv.com</Link>
        </div>
      )}
      {staff && !done && (
        <form onSubmit={submit} className="w-full max-w-sm text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--trio-primary)] font-bold">Eat. Drink. Vibe.</div>
          <h1 data-testid="staff-review-title" className="font-display uppercase text-4xl tracking-tight mt-2 leading-none">How Was {first}?</h1>
          {staff.position && <div className="text-xs uppercase tracking-widest text-[var(--trio-muted)] mt-2">{staff.position}</div>}
          <div className="flex justify-center gap-1 mt-8" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                data-testid={`staff-review-star-${n}`}
                className="p-1 transition-transform active:scale-90"
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
              >
                <Star className={`w-11 h-11 ${n <= shown ? "fill-[var(--trio-accent)] text-[var(--trio-accent)]" : "text-[var(--trio-border)]"}`} />
              </button>
            ))}
          </div>
          <div data-testid="staff-review-label" className="h-6 mt-2 text-sm font-semibold">{LABELS[shown]}</div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 600))}
            placeholder={`Tell us about your experience with ${first} (optional)`}
            data-testid="staff-review-comment"
            className="mt-6 rounded-none border-[var(--trio-border)] bg-white min-h-[110px]"
          />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            placeholder="Your name (optional)"
            data-testid="staff-review-name"
            className="mt-3 rounded-none border-[var(--trio-border)] bg-white"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="staff-review-submit"
            className="mt-6 w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-4 text-sm uppercase tracking-widest font-bold disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send Review"}
          </button>
        </form>
      )}
    </div>
  );
}
